import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Viewport-aware tooltip rendered via portal so it's never clipped by overflow parents.
 * Measures the anchor button position and clamps the tooltip within viewport edges.
 */
function CitationTooltip({ anchorRef, visible, children, width = 320 }) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!visible || !anchorRef?.current) {
      setPos(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const pad = 8;
    const halfW = width / 2;
    const anchorCenterX = rect.left + rect.width / 2;

    let left = anchorCenterX - halfW;
    let arrowLeft = halfW;

    if (left < pad) {
      arrowLeft = anchorCenterX - pad;
      left = pad;
    } else if (left + width > window.innerWidth - pad) {
      left = window.innerWidth - pad - width;
      arrowLeft = anchorCenterX - left;
    }

    arrowLeft = Math.max(12, Math.min(arrowLeft, width - 12));

    setPos({
      top: rect.top - 8,
      left,
      arrowLeft: `${arrowLeft}px`,
    });
  }, [visible, anchorRef, width]);

  if (!visible || !pos) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 99999,
        width: `${width}px`,
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        transform: 'translateY(-100%)',
        pointerEvents: 'none',
      }}
      className="bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3"
    >
      {children}
      <div
        className="absolute"
        style={{ top: '100%', left: pos.arrowLeft, transform: 'translateX(-50%)', marginTop: '-1px' }}
      >
        <div className="w-2 h-2 bg-gray-900 rotate-45 transform"></div>
      </div>
    </div>,
    document.body
  );
}

/**
 * ReadMoreText - Component for displaying long text with "Read more/Read less" functionality
 * Supports basic markdown formatting, [N] and [N-M] transcript citations, and [Sn] source citations.
 */
export default function ReadMoreText({ text, maxLength = 500, className = '', transcriptReferences, sourceReferences, onCitationClick }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredCitation, setHoveredCitation] = useState(null);
  const buttonRefs = useRef({});

  const getButtonRef = useCallback((key) => {
    if (!buttonRefs.current[key]) {
      buttonRefs.current[key] = { current: null };
    }
    return buttonRefs.current[key];
  }, []);

  if (!text || (typeof text === 'string' && text.trim().length === 0)) {
    return (
      <div className={`text-gray-500 italic ${className}`}>
        No content available
      </div>
    );
  }

  const textStr = String(text).replace(/[\u2011\u2013\u2014]/g, '-');
  const shouldTruncate = textStr.length > maxLength;
  let displayText = isExpanded || !shouldTruncate ? textStr : `${textStr.substring(0, maxLength)}...`;

  const processInlineMarkdown = (line) => {
    let processed = line;
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    processed = processed.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    return processed;
  };

  const mergeConsecutiveCitations = (parts) => {
    const merged = [];
    let pendingNums = [];

    const flushPending = () => {
      if (pendingNums.length === 0) return;
      if (pendingNums.length === 1) {
        merged.push({ type: 'transcript', start: pendingNums[0], end: pendingNums[0] });
      } else {
        merged.push({ type: 'transcript', start: pendingNums[0], end: pendingNums[pendingNums.length - 1] });
      }
      pendingNums = [];
    };

    for (const part of parts) {
      if (part.type === 'transcript') {
        if (part.start === part.end) {
          const num = part.start;
          if (pendingNums.length === 0 || num === pendingNums[pendingNums.length - 1] + 1) {
            pendingNums.push(num);
          } else {
            flushPending();
            pendingNums.push(num);
          }
        } else {
          flushPending();
          merged.push(part);
        }
      } else {
        flushPending();
        merged.push(part);
      }
    }
    flushPending();
    return merged;
  };

  const tokenize = (html) => {
    // Normalize unbracketed source refs: (S13, S14) → [S13] [S14], Source S99 → [S99], etc.
    let normalized = html;
    normalized = normalized.replace(/\s*\[transcript\]/gi, '');
    if (sourceReferences) {
      // First: convert grouped patterns like "(S13, S14)" or "(S15)" into "[S13] [S14]" / "[S15]"
      normalized = normalized.replace(/\(S\d+(?:\s*,\s*S\d+)*\)/g, (match) => {
        return [...match.matchAll(/S(\d+)/g)].map(m => `[S${m[1]}]`).join(' ');
      });
      // Then: convert remaining standalone S<num> not already in brackets
      normalized = normalized.replace(/(?<!\[)\bS(\d+)\b(?!\])/g, '[S$1]');
    }
    const citRegex = /((?:\[\d+(?:-\d+)?\]|【\d+(?:-\d+)?】|\[S\d+\]))/g;
    const rawParts = normalized.split(citRegex);
    const tokens = [];

    for (const part of rawParts) {
      if (!part) continue;

      const srcMatch = part.match(/^\[S(\d+)\]$/);
      if (srcMatch) {
        tokens.push({ type: 'source', num: parseInt(srcMatch[1], 10), raw: part });
        continue;
      }

      const rangeMatch = part.match(/^(?:\[(\d+)-(\d+)\]|【(\d+)-(\d+)】)$/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1] || rangeMatch[3], 10);
        const end = parseInt(rangeMatch[2] || rangeMatch[4], 10);
        tokens.push({ type: 'transcript', start, end, raw: part });
        continue;
      }

      const singleMatch = part.match(/^(?:\[(\d+)\]|【(\d+)】)$/);
      if (singleMatch) {
        const num = parseInt(singleMatch[1] || singleMatch[2], 10);
        tokens.push({ type: 'transcript', start: num, end: num, raw: part });
        continue;
      }

      tokens.push({ type: 'text', content: part });
    }

    return mergeConsecutiveCitations(tokens);
  };

  const renderWithCitations = (html, keyPrefix) => {
    const tokens = tokenize(html);

    if (tokens.length === 1 && tokens[0].type === 'text') {
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    }

    return tokens.map((token, i) => {
      if (token.type === 'source') {
        const srcNum = token.num;
        const ref = sourceReferences ? sourceReferences[String(srcNum)] : null;
        const btnKey = `${keyPrefix}-S${srcNum}-${i}`;
        const btnRef = getButtonRef(btnKey);

        return (
          <span key={`${keyPrefix}-src-${i}`} className="inline">
            <button
              ref={(el) => { btnRef.current = el; }}
              onClick={(e) => {
                e.preventDefault();
                if (ref && ref.url) {
                  window.open(ref.url, '_blank', 'noopener,noreferrer');
                }
              }}
              onMouseEnter={() => setHoveredCitation(btnKey)}
              onMouseLeave={() => setHoveredCitation(null)}
              className="inline-flex items-center justify-center text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1 py-0 mx-0.5 hover:bg-teal-100 hover:text-teal-800 hover:border-teal-300 transition-all cursor-pointer align-super leading-none"
              style={{ fontSize: '10px', lineHeight: '14px', minWidth: '22px', verticalAlign: 'super' }}
            >
              S{srcNum}
            </button>
            <CitationTooltip anchorRef={btnRef} visible={hoveredCitation === btnKey && !!ref}>
              <div className="font-semibold text-teal-300 mb-1">[S{srcNum}] {ref?.title}</div>
              {ref?.snippet && <div className="text-gray-300 leading-relaxed italic mb-1">{ref.snippet.substring(0, 200)}{ref.snippet.length > 200 ? '...' : ''}</div>}
              {ref?.url && <div className="text-teal-400 text-[10px] truncate">&#x1F517; {ref.url}</div>}
            </CitationTooltip>
          </span>
        );
      }

      if (token.type === 'transcript') {
        const { start, end } = token;
        const isRange = start !== end;
        const label = isRange ? `${start}-${end}` : `${start}`;
        const btnKey = `${keyPrefix}-t-${start}-${end}-${i}`;
        const btnRef = getButtonRef(btnKey);

        const buildTooltipContent = () => {
          if (!transcriptReferences) return null;
          if (!isRange) {
            const ref = transcriptReferences[String(start)];
            if (!ref) return null;
            return (
              <>
                <div className="font-semibold text-primary-300 mb-1">[{start}] {ref.speaker} &middot; {ref.start}</div>
                <div className="text-gray-300 leading-relaxed italic">&ldquo;{ref.sentence || ref.text_preview}&rdquo;</div>
              </>
            );
          }
          const firstRef = transcriptReferences[String(start)];
          const lastRef = transcriptReferences[String(end)];
          if (!firstRef && !lastRef) return null;
          const sentenceCount = end - start + 1;
          return (
            <>
              <div className="font-semibold text-primary-300 mb-1">[{start}-{end}] {sentenceCount} sentences</div>
              {firstRef && (
                <div className="text-gray-300 leading-relaxed italic mb-1">
                  <span className="text-primary-400 font-medium">[{start}]</span> {firstRef.speaker} ({firstRef.start}): &ldquo;{(firstRef.sentence || firstRef.text_preview || '').substring(0, 100)}&rdquo;
                </div>
              )}
              {sentenceCount > 2 && <div className="text-gray-500 text-[10px] mb-1">... {sentenceCount - 2} more sentence{sentenceCount - 2 > 1 ? 's' : ''} ...</div>}
              {lastRef && start !== end && (
                <div className="text-gray-300 leading-relaxed italic">
                  <span className="text-primary-400 font-medium">[{end}]</span> {lastRef.speaker} ({lastRef.start}): &ldquo;{(lastRef.sentence || lastRef.text_preview || '').substring(0, 100)}&rdquo;
                </div>
              )}
            </>
          );
        };

        return (
          <span key={`${keyPrefix}-cit-${i}`} className="inline">
            <button
              ref={(el) => { btnRef.current = el; }}
              onClick={(e) => {
                e.preventDefault();
                if (onCitationClick) onCitationClick(start, end);
              }}
              onMouseEnter={() => setHoveredCitation(btnKey)}
              onMouseLeave={() => setHoveredCitation(null)}
              className="inline-flex items-center justify-center text-[10px] font-bold text-primary-700 bg-primary-50 border border-primary-200 rounded px-1 py-0 mx-0.5 hover:bg-primary-100 hover:text-primary-800 hover:border-primary-300 transition-all cursor-pointer align-super leading-none"
              style={{ fontSize: '10px', lineHeight: '14px', minWidth: '18px', verticalAlign: 'super' }}
            >
              {label}
            </button>
            <CitationTooltip anchorRef={btnRef} visible={hoveredCitation === btnKey && !!transcriptReferences}>
              {buildTooltipContent()}
            </CitationTooltip>
          </span>
        );
      }

      return <span key={`${keyPrefix}-txt-${i}`} dangerouslySetInnerHTML={{ __html: token.content }} />;
    });
  };

  const formatMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const hasCitations = (transcriptReferences && onCitationClick) || sourceReferences;

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('### ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h3 key={`h3-${index}`} className="text-lg font-semibold mb-2 mt-4 text-gray-900">
            {trimmed.replace('### ', '')}
          </h3>
        );
      } else if (trimmed.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h2 key={`h2-${index}`} className="text-xl font-semibold mb-3 mt-5 text-gray-900">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('# ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h1 key={`h1-${index}`} className="text-2xl font-bold mb-4 mt-6 text-gray-900">
            {trimmed.replace('# ', '')}
          </h1>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (!inList) {
          inList = true;
        }
        const listContent = processInlineMarkdown(trimmed.substring(2));
        if (hasCitations && (/\[\d+(?:-\d+)?\]/.test(listContent) || /【\d+(?:-\d+)?】/.test(listContent) || /\[S\d+\]/.test(listContent) || (sourceReferences && /\bS\d+\b/.test(listContent)))) {
          listItems.push(
            <li key={`li-${index}`} className="mb-1">{renderWithCitations(listContent, `li-${index}`)}</li>
          );
        } else {
          listItems.push(
            <li key={`li-${index}`} className="mb-1" dangerouslySetInnerHTML={{ __html: listContent }} />
          );
        }
      } else if (trimmed === '') {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(<br key={`br-${index}`} />);
      } else {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const processedLine = processInlineMarkdown(trimmed);
        if (hasCitations && (/\[\d+(?:-\d+)?\]/.test(processedLine) || /【\d+(?:-\d+)?】/.test(processedLine) || /\[S\d+\]/.test(processedLine) || (sourceReferences && /\bS\d+\b/.test(processedLine)))) {
          elements.push(
            <p key={`p-${index}`} className="mb-4 text-base leading-7 text-gray-800">
              {renderWithCitations(processedLine, `p-${index}`)}
            </p>
          );
        } else {
          elements.push(
            <p
              key={`p-${index}`}
              className="mb-4 text-base leading-7 text-gray-800"
              dangerouslySetInnerHTML={{ __html: processedLine }}
            />
          );
        }
      }
    });

    if (inList && listItems.length > 0) {
      elements.push(<ul key="ul-final" className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
    }

    return elements;
  };

  return (
    <div className={className}>
      <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed break-words">
        {formatMarkdown(displayText)}
      </div>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        >
          {isExpanded ? (
            <>
              <span>Read less</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </>
          ) : (
            <>
              <span>Read more</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
}
